import React, { lazy, Suspense } from 'react';
import { TAPStatus } from '@sharefiledev/esign-pilet-sdk';
import { LoadingPrimary } from '@sharefiledev/flow-web';
import { RID } from '@sharefiledev/sfplugin-resource-id';
import { PiletApi } from '@sharefiledev/sharefile-appshell';
import { DocumentSendSignatureRequestDrawerProps } from '../Common/Components/types';
import { SignInSource, UserAction } from '../constants';
import { PiletApiContextProvider } from '../Contexts/PiletApiContext';
import { ShareFileUserContextProvider } from '../Contexts/ShareFileUserContext';
import { ReactQueryClientProvider } from '../data/ReactQueryClientProvider';
import { findUserAction, getSigner } from '../util/SignerAction';

const LazySignatureRequestTAPDrawer = lazy(() => import('./SignatureRequestTAPDrawer'));
export const AsyncSignatureRequestTAPDrawer: React.FC<{ piletApi: PiletApi }> = props => {
	return (
		<Suspense fallback={<LoadingPrimary />}>
			<ShareFileUserContextProvider {...props.piletApi.sf.piletUserContext.get()}>
				<ReactQueryClientProvider>
					<PiletApiContextProvider piletApi={props.piletApi}>
						<LazySignatureRequestTAPDrawer {...props} />
					</PiletApiContextProvider>
				</ReactQueryClientProvider>
			</ShareFileUserContextProvider>
		</Suspense>
	);
};

const LazySendForSignatureDrawer = lazy(() => import('./SendForSignatureDrawer'));
export const AsyncSendForSignatureDrawer: React.FC<{
	piletApi: PiletApi;
	isCreateFromTemplate?: boolean;
}> = props => {
	return (
		<ShareFileUserContextProvider {...props.piletApi.sf.piletUserContext.get()}>
			<LazySendForSignatureDrawer {...props} />
		</ShareFileUserContextProvider>
	);
};

const LazyClientSignerTAPDrawer = lazy(
	() => import('../Dashboard/DashboardSign/DashboardSignView')
);
const LazyPreviewPage = lazy(() => import('../PreviewPage/PreviewPage'));

export const AsyncClientSignerTAPDrawer: React.FC<{
	piletApi: PiletApi;
	params: {
		taskDetails: { assignees: any[]; data: Record<string, any>; status: TAPStatus };
		containerRID: string;
	};
	cancelInteraction: () => void;
}> = props => {
	const { containerRID: taskRID } = props.params;
	const parsedRID = RID.parse(taskRID);

	const sfUser = props.piletApi?.sf.piletUserContext.get()?.user;
	const signer = getSigner(sfUser?.Emails, props.params?.taskDetails?.data?.signers);
	const userAction = findUserAction(props.params?.taskDetails?.status, signer);
	return (
		<Suspense fallback={<LoadingPrimary />}>
			<PiletApiContextProvider piletApi={props.piletApi}>
				<ReactQueryClientProvider>
					<ShareFileUserContextProvider {...props.piletApi.sf.piletUserContext.get()}>
						{userAction === UserAction.PendingSignature ? (
							<LazyClientSignerTAPDrawer
								{...props}
								signInSource={SignInSource.ClientPortal}
							/>
						) : (
							<LazyPreviewPage {...props} documentId={parsedRID.entityId} />
						)}
					</ShareFileUserContextProvider>
				</ReactQueryClientProvider>
			</PiletApiContextProvider>
		</Suspense>
	);
};

const LazyDocumentSendSignatureRequestDrawer = lazy(
	() => import('./DocumentSendSignatureRequestDrawer/DocumentSendSignatureRequestDrawer')
);
export const AsyncDocumentSendSignatureRequestDrawer: React.FC<{
	piletApi: PiletApi;
	params: DocumentSendSignatureRequestDrawerProps;
	cancelInteraction: () => void;
}> = props => {
	return (
		<Suspense fallback={<LoadingPrimary />}>
			<ReactQueryClientProvider>
				<ShareFileUserContextProvider {...props.piletApi.sf.piletUserContext.get()}>
					<LazyDocumentSendSignatureRequestDrawer
						{...props.params}
						cancelInteraction={props.cancelInteraction}
					/>
				</ShareFileUserContextProvider>
			</ReactQueryClientProvider>
		</Suspense>
	);
};
